import { Modal, notification, Steps } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useStore } from 'store/useStore'
import { PromoPageKeys } from 'sections/PromoSection/pages/PromoPage/types'
import { promoColors } from 'sections/PromoSection/shared/constants'
import { Margin, TextPromo } from 'components/ui'
import { Container } from 'components/ui/Container/Container'
import { Button, Flex, Title } from 'components/uiKit'
import { isGeDomain } from 'utils/helpers/domains/getDomain'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'
import { sendEvent } from 'utils/helpers/sendEvent'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { useResize } from 'utils/hooks/useResize'
import { AuthRoutesEnum, PromoGeRoutesEnum } from 'shared/constants/routes'
import { URLS } from 'shared/constants/urls'
import { SEARCH_PARAMS } from 'shared/constants/values'
import { Country } from 'shared/types/types'
import { EventType } from 'types/types'

import { ActivityStep } from '../../components/PersonalDataSteps/ActivityStep/ActivityStep'
import { ContactInfoStep } from '../../components/PersonalDataSteps/ContactInfoStep/ContactInfoStep'
import { PassportPhotoStep } from '../../components/PersonalDataSteps/PassportPhotoStep/PassportPhotoStep'
import { PassportStep } from '../../components/PersonalDataSteps/PassportStep/PassportStep'
import { STEPS_TITLES } from '../../data'
import { Citizenship, DocumentType, PersonalDataStep, UserModuleKeys } from '../../types'

import s from './RegistrationPage.module.scss'

export const RegistrationPage = observer(() => {
  const { registrationUiStore: uiStore, appStore, offerStore, loginUiStore } = useStore()
  const { isMobile } = useResize()
  const [isOfertaVisible, setOfertaVisible] = useState<boolean>(false)
  const [isOfertaPageLoading, setOfertaPageLoading] = useState<boolean>(true)
  const [stage, setStage] = useState(0)
  const navigate = useNavigate()

  const [searchParams, setSearchParams] = useSearchParams()
  const { t } = useTranslation()

  const getKey = (key: string) => `${UserModuleKeys.regisgtrationPage}.${key}`
  const getDataKey = (key: string) => `${UserModuleKeys.data}.${key}`
  const getKeyPromo = (key: string) => `${PromoPageKeys.openPickPointForm}.${key}`

  const mobileHash = searchParams.get(SEARCH_PARAMS.mobileHash)
  const isPassport = uiStore.companyDetails.documentType === DocumentType.passport
  const stepsTitles = STEPS_TITLES.map((value) => {
    const title = t(getDataKey(value.title as string))
    return { title }
  })

  useAsyncEffect(async () => {
    if (appStore.user?.info.user_id === 0) {
      if (mobileHash) {
        searchParams.delete(SEARCH_PARAMS.mobileHash)
        setSearchParams(searchParams)
        await loginUiStore.useAuthHash(mobileHash)
      }

      await appStore.bootstrap()

      if (!appStore.isLoadingUserInfo && !appStore.user.authenticated) {
        navigate(AuthRoutesEnum.login, { replace: true })
        return
      }
    }

    notification.info({ message: t(getKey('Для доступа к личному')) })
  }, [])

  // Для грузинского домена устанавливаем по умолчанию Грузию
  useEffect(() => {
    if (isGeDomain()) {
      uiStore.companyDetails.setSelectedCountry({ label: 'Грузия', value: '995', country: Country.GE })
      uiStore.companyDetails.setSelectedCitizenship({ label: 'Грузия', value: Citizenship.GE })
      uiStore.companyDetails.setDocumentType(DocumentType.identification)
      uiStore.profile.additionalPhoneModel.setSelectedCountry({ label: 'Грузия', value: '995', country: Country.GE })
    }
  }, [])

  useEffect(() => {
    if (uiStore.personalDataStep === PersonalDataStep.step4) {
      offerStore.apiStore.getOfferVersion(uiStore.companyDetails.selectedCountry.country)
    }
  }, [uiStore.personalDataStep])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [uiStore.personalDataStep])

  const handleNextStep = () => {
    switch (uiStore.personalDataStep) {
      case PersonalDataStep.step1:
        sendEvent(EventType.registration, { step: 1, success: 1 })
        uiStore.setPersonalDataStep(PersonalDataStep.step2)
        break
      case PersonalDataStep.step2:
        sendEvent(EventType.registration, { step: 2, success: 1 })
        uiStore.setPersonalDataStep(PersonalDataStep.step3)
        break
      case PersonalDataStep.step3:
        sendEvent(EventType.registration, { step: 3, success: 1 })
        uiStore.setPersonalDataStep(PersonalDataStep.step4)
        break
      case PersonalDataStep.step4:
        setOfertaVisible(true)
        break
    }
  }

  const handlePrevStep = () => {
    switch (uiStore.personalDataStep) {
      case PersonalDataStep.step1:
        appStore.logout()
        localStorage.clear()
        navigate(isGeDomain() ? PromoGeRoutesEnum.login : AuthRoutesEnum.login, { replace: true })
        break
      case PersonalDataStep.step2:
        uiStore.setPersonalDataStep(PersonalDataStep.step1)
        break
      case PersonalDataStep.step3:
        uiStore.setPersonalDataStep(PersonalDataStep.step2)
        break
      case PersonalDataStep.step4:
        uiStore.setPersonalDataStep(PersonalDataStep.step3)
        break
    }
  }

  const handleAcceptPress = async () => {
    let nextStage = stage

    if (stage < offerStore.linksForRegistration.length - 1) {
      setOfertaPageLoading(true)
      nextStage = nextStage + 1
    } else {
      await offerStore.apiStore
        .signOfferVersion({
          version: offerStore.offers[0].lastVersion as number,
          countryCode: offerStore.offers[0].countryCode,
        })
        .catch((error) => {
          sendEvent(EventType.registration, { step: 4, success: 0, reason: 'Ошибка подписания оферты' })
          throw error
        })

      await uiStore.apiStore.tryRegisterOwner().catch((error) => {
        sendEvent(EventType.registration, { step: 4, success: 0, reason: (error as Error).message })
        throw error
      })

      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          isRegistrationSuccess: true,
        }),
      )
      sendEvent(EventType.registration, { step: 4, success: 1 })
      navigate(AuthRoutesEnum.login, { replace: true })
      uiStore.setPersonalDataStep(PersonalDataStep.step1)
      uiStore.companyDetails.clearCompanyInfo()
      setOfertaVisible(false)
      nextStage = 0
    }

    setStage(nextStage)
  }

  const cancelOffer = () => {
    setOfertaVisible(false)
    setStage(0)
  }

  const isDisabledNextStep = (): boolean => {
    switch (uiStore.personalDataStep) {
      case PersonalDataStep.step1:
        return uiStore.validateStore.isInvalidRequisitesData
      case PersonalDataStep.step2:
        return isPassport
          ? uiStore.validateStore.isInvalidPassportForm
          : uiStore.validateStore.isInvalidIdentificationForm
      case PersonalDataStep.step3:
        return uiStore.companyDetails.isInvalidDocumentPhoto || uiStore.companyDetails.isInvalidWithDocumentPhoto
      case PersonalDataStep.step4:
        return uiStore.validateStore.isInvalidContactInfo
    }
  }

  return (
    <Container>
      <Flex className={s.RegistrationPage} vertical>
        <Margin mt={isMobile ? 50 : 150} mb={isMobile ? 20 : 80}>
          <Title>{t(getKey('Регистрация'))} </Title>
        </Margin>

        <Steps current={uiStore.personalDataStep} items={stepsTitles} size={'small'} direction={'vertical'} />

        <Flex vertical style={{ maxWidth: 600 }}>
          {uiStore.personalDataStep === PersonalDataStep.step1 && (
            <ActivityStep companyDetails={uiStore.companyDetails} />
          )}
          {uiStore.personalDataStep === PersonalDataStep.step2 && (
            <PassportStep companyDetails={uiStore.companyDetails} profile={uiStore.profile} />
          )}
          {uiStore.personalDataStep === PersonalDataStep.step3 && (
            <PassportPhotoStep companyDetails={uiStore.companyDetails} />
          )}
          {uiStore.personalDataStep === PersonalDataStep.step4 && (
            <ContactInfoStep profile={uiStore.profile} citizenship={uiStore.companyDetails.selectedCitizenship.value} />
          )}

          {isGeDomain() && (
            <Margin mt={44} mb={40}>
              <TextPromo center fontFamily="ASLHaussVF" size="s16" weight="w400">
                {t(getKey('Нажимая «Далее», я подтверждаю ознакомление с'), {
                  buttonTitle:
                    uiStore.personalDataStep === PersonalDataStep.step4
                      ? t(getKey('К оферте'))
                      : t(getSharedKey('next')),
                })}{' '}
                <a
                  href={isGeDomain() ? URLS.wbMarketPrivatePolicyGe : URLS.wbMarketPrivatePolicy}
                  target="_blank"
                  style={{ fontWeight: 400, color: promoColors.newBgPrimary, textDecoration: 'none' }}
                  rel="noreferrer"
                >
                  {t(getKeyPromo('политикой конфиденциальности'))}
                </a>
              </TextPromo>
            </Margin>
          )}

          <Flex>
            <Flex vertical>
              <Button
                onClick={handleNextStep}
                disabled={isDisabledNextStep() || uiStore.isRegistrationLoading}
                loading={uiStore.isRegistrationLoading}
              >
                {uiStore.personalDataStep === PersonalDataStep.step4 ? t(getKey('К оферте')) : t(getSharedKey('next'))}
              </Button>
              <Margin mt={20}>
                <Button type={'default'} onClick={handlePrevStep} disabled={uiStore.isRegistrationLoading}>
                  {uiStore.personalDataStep === PersonalDataStep.step1
                    ? t(getKey('Войти под другим номером телефона'))
                    : t(getSharedKey('back'))}
                </Button>
              </Margin>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <Modal
        title={t(getKey('Оферта'))}
        open={isOfertaVisible}
        onOk={handleAcceptPress}
        okText={stage === offerStore.linksForRegistration.length - 1 ? t(getKey('Принять')) : t(getSharedKey('next'))}
        cancelText={t(getKey('Отклонить'))}
        okButtonProps={{ loading: offerStore.loading || uiStore.isRegistrationLoading || isOfertaPageLoading }}
        onCancel={cancelOffer}
        width={'90vw'}
      >
        <iframe
          style={{ width: '100%', height: '70vh' }}
          onLoad={() => setOfertaPageLoading(false)}
          src={offerStore.linksForRegistration[stage]}
        />
      </Modal>
    </Container>
  )
})
